/**
 * The header's language menu (ADR-128 Amendment 4): Automatic first, then the
 * five languages the whole experience speaks, each in its own language.
 *
 * A disclosure, not an ARIA `menu`: the button that opens it says
 * `aria-expanded`, and inside is a plain group of buttons, each saying with
 * `aria-pressed` whether it is the one in force. Tab reaches them, Enter and
 * Space press them, and Escape and a press outside close the group — the info
 * panel's behaviour, so the header has one way of working.
 */
import type { RefObject } from 'react';
import { CheckIcon } from './icons';
import { CHOOSABLE, ENDONYMS, type Choosable } from './languages';
import type { WidgetStrings } from './strings';
import type { Styles } from './theme';

interface Props {
  ui: WidgetStrings;
  styles: Styles;
  /** The visitor's choice; '' = Automatic. */
  chosen: Choosable | '';
  /** What Automatic is using right now, in its own name (the conversation's, else the page's). */
  automaticNow: string;
  onChoose: (choice: Choosable | '') => void;
  panelRef: RefObject<HTMLDivElement | null>;
}

export function LanguageMenu({ ui, styles, chosen, automaticNow, onChoose, panelRef }: Props) {
  const item = (selected: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
    padding: '7px 10px',
    borderRadius: '10px',
    border: 'none',
    background: selected ? styles.chipHoverBackground : 'transparent',
    color: selected ? styles.accent : '#1a1a2e',
    fontWeight: selected ? 600 : 500,
    fontSize: '13px',
    fontFamily: 'inherit',
    textAlign: 'left' as const,
    cursor: 'pointer',
  });

  return (
    <div
      ref={panelRef}
      style={{ ...styles.infoPanel, width: '230px', gap: '4px', padding: '10px 8px' }}
      role="group"
      aria-label={ui.languageMenuTitle}
      data-pwe="language-menu"
    >
      <div style={{ fontWeight: 600, color: '#1a1a2e', padding: '0 10px 4px' }}>{ui.languageMenuTitle}</div>
      <button
        type="button"
        onClick={() => onChoose('')}
        style={item(chosen === '')}
        aria-pressed={chosen === ''}
        data-pwe="language-option"
        data-value="auto"
      >
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span>
            {ui.languageAutomatic}
            {automaticNow ? ` (${automaticNow})` : ''}
          </span>
          <span style={{ fontSize: '11px', fontWeight: 400, color: '#6b7280' }}>{ui.languageAutomaticHint}</span>
        </span>
        {chosen === '' && <CheckIcon />}
      </button>
      <div role="separator" style={{ height: '1px', background: 'rgba(0,0,0,0.06)', margin: '2px 6px' }} />
      {CHOOSABLE.map((tag) => (
        <button
          key={tag}
          type="button"
          lang={tag}
          onClick={() => onChoose(tag)}
          style={item(chosen === tag)}
          aria-pressed={chosen === tag}
          data-pwe="language-option"
          data-value={tag}
        >
          <span>{ENDONYMS[tag]}</span>
          {chosen === tag && <CheckIcon />}
        </button>
      ))}
    </div>
  );
}
